import {formatDecimal} from "../tableConfig";

/**
 * Cell formatting for the map pack validation summary, kept apart from the table config so that it can be
 * unit tested without pulling in the component library.
 *
 * All of it exists to keep one distinction: a value the recost job did not produce is not the same as a
 * value of zero, and on this page the difference decides whether a map pack looks healthy.
 */

/** Shown wherever a summary row carries no value, so that absent never renders as zero. */
export const NO_VALUE = "n/a";

/** Shown when a ratio has no value because its denominator was zero. */
export const NO_VALUE_ZERO_CONTROL = "n/a (control 0)";

/**
 * Formats a count that a summary row may not carry at all.
 *
 * A build that ran before a counter existed has no value for it, and printing 0 would claim a measurement
 * that was never taken. Zero itself is a real measurement and still renders as "0".
 *
 * @param value the count, or null when the row does not carry it
 * @returns the count as text, or {@link NO_VALUE}
 */
export function formatCount(value: number | null): string {
    return value == null ? NO_VALUE : String(value);
}

/**
 * Formats a ratio the recost job derives by dividing by a control value, and cannot produce when that
 * control value is zero.
 *
 * Two such ratios are shown on this page, and both are undefined for the same reason:
 * {@code pnfPercentIncrease}, derived as {@code (experiment - control) * 100 / control} over the on-zone
 * path-not-found counts, and {@code dpmoChange}, derived as {@code (dpmo - dpmoControl) / dpmoControl}.
 *
 * Neither may render as 0. A control of zero with a non-zero experiment is the worst outcome there is - a
 * map pack that broke routes which previously worked - and 0 reads as "no change".
 *
 * Why the control value decides the wording rather than the missing value itself: the API serializes rows
 * with a default Gson instance, which omits nulls, so a stored null and an attribute that was never
 * written both arrive here as undefined and cannot be told apart. The control value can tell them apart,
 * because a control of zero is the only input that makes the ratio undefined.
 *
 * @param value the ratio, or null when the row does not carry it
 * @param control the denominator the ratio was derived from
 * @returns the ratio to two decimal places, or a wording that says why there is none
 */
export function formatRatio(value: number | null, control: number | null): string {
    const formatted = formatDecimal(value);
    if (formatted !== "") {
        return formatted;
    }
    return control === 0 ? NO_VALUE_ZERO_CONTROL : NO_VALUE;
}

/** Shown when a summary row carries no recost gate verdict at all. Never an empty cell. */
export const VERDICT_NOT_EVALUATED = "Not evaluated";

/** The verdict values MapWorkflowOrchestrator writes. Anything else is shown verbatim. */
export const VERDICT_WITHIN_LIMIT = "WITHIN_LIMIT";
export const VERDICT_OVER_LIMIT = "OVER_LIMIT";
export const VERDICT_NOT_JUDGED = "NOT_JUDGED";

/** How one map pack's verdict should be shown. */
export interface VerdictDisplay {
    /** Badge text. */
    label: string;
    /** Badge colour, restricted to what the component library accepts. */
    color: "green" | "red" | "grey";
    /**
     * Whether to show the recorded reason beside the badge. True only when there is something to act on:
     * repeating "within the limit" on every healthy row is noise, while a blocked or unjudged pack is
     * useless without the numbers behind it.
     */
    showReason: boolean;
}

/**
 * Turns the verdict the recost gate recorded into how it should be shown.
 *
 * Four states, and the fourth is not the third. An absent verdict means the gate never wrote one - every
 * pre-existing row is in that state, and a Glue re-run erases it again - which is not the gate declining to
 * judge. Blank must never read as "the gate is happy", so absent is labelled explicitly. An unrecognised
 * value is shown verbatim for the same reason.
 *
 * No rule is applied here; the threshold comparison stays in the gate.
 *
 * @param verdict the stored verdict, or null when the row carries none
 * @returns the label, badge colour and whether to show the reason
 */
export function verdictDisplay(verdict: string | null): VerdictDisplay {
    switch (verdict) {
        case VERDICT_WITHIN_LIMIT:
            return {label: "Within limit", color: "green", showReason: false};
        case VERDICT_OVER_LIMIT:
            return {label: "Over limit", color: "red", showReason: true};
        case VERDICT_NOT_JUDGED:
            return {label: "Not judged", color: "grey", showReason: true};
        default:
            if (verdict == null || verdict === "") {
                return {label: VERDICT_NOT_EVALUATED, color: "grey", showReason: false};
            }
            return {label: verdict, color: "grey", showReason: true};
    }
}
